/*
 * WorkAndBuhin.vue の日整連・純正部品・型式検索に関する API 呼び出しを切り出した処理。
 */
import { KatashikiParams } from "@/model/KatashikiParams";
import {
  buildJunseiBuhinByNisseirenWorkParams,
  buildKatashikiUpdatePayload
} from "@/components/work-buhin/WorkBuhinNisseiren";

export interface WorkBuhinNisseirenApi {
  getNisseirenWorkByKatashiki(
    modelCd: any,
    katashiki: any,
    engineKatashiki: any,
    kakyukiKbn: any,
    sknUketsukeExtendsId: number,
    sknInspectDescriptionId: number,
    sknSeibiShubetsuKbn: number
  ): Promise<any>;
  getJunseiBuhinByKatashiki(
    katashikiShiteiNo: any,
    ruibetsuKubunNo: any,
    katashiki: any,
    engineKatashiki: any,
    syaryouShiki: any,
    syaryouShuki: any,
    sknInspectDescriptionId: number
  ): Promise<any>;
  getJunseiBuhinByNisseirenWork(
    katashikiShiteiNo: any,
    ruibetsuKubunNo: any,
    katashiki: any,
    engineKatashiki: any,
    syaryouShiki: any,
    syaryouShuki: any,
    nisseirenCd: any
  ): Promise<any>;
  getKatashikiList(
    katashiki: string,
    engineKatashiki: string,
    katashikiShiteiNo: string,
    ruibetsuKubunNo: string
  ): Promise<any>;
  getCustomerCarKatashiki(sknUketsukeExtendsId: number): Promise<any>;
  updateKatashiki(data: any): Promise<any>;
}

export interface FetchNisseirenWorkByKatashikiParams {
  katashikiParams: Partial<KatashikiParams>;
  uketsukeExtendsId: number;
  sknInspectDescriptionId: number;
  sknSeibiShubetsuKbn: number;
}

export interface FetchJunseiBuhinByKatashikiParams {
  katashikiParams: Partial<KatashikiParams>;
  sknInspectDescriptionId: number;
}

export interface FetchJunseiBuhinByNisseirenWorkParams {
  katashikiParams: Partial<KatashikiParams>;
  sknNisseirenCd: any;
}

export interface JunseiBuhinByNisseirenWorkResult {
  found: boolean;
  result: any;
}

export interface KatashikiSearchResult {
  data: any | null;
  error: any;
}

function resolveNoContentAsEmpty(err: any): any[] | undefined {
  if (err && err.status === 204) {
    return [];
  }
  return undefined;
}

/*
 * fetchNisseirenWorkByKatashiki を WorkAndBuhin.vue から分離し、
 * 型式・日整連・純正 API 呼び出し を親側置換や単体テストで固定するため。
 */
export function fetchNisseirenWorkByKatashiki(
  api: WorkBuhinNisseirenApi,
  params: FetchNisseirenWorkByKatashikiParams
): Promise<any> {
  return api.getNisseirenWorkByKatashiki(
    params.katashikiParams.modelCd,
    params.katashikiParams.nisseirenKatashiki,
    params.katashikiParams.nisseirenEngineKatashiki,
    params.katashikiParams.kakyukiKbn,
    params.uketsukeExtendsId,
    params.sknInspectDescriptionId,
    params.sknSeibiShubetsuKbn
  ).catch((err: any) => resolveNoContentAsEmpty(err));
}

/*
 * fetchJunseiBuhinByKatashiki を WorkAndBuhin.vue から分離し、
 * 型式・日整連・純正 API 呼び出し を親側置換や単体テストで固定するため。
 */
export function fetchJunseiBuhinByKatashiki(
  api: WorkBuhinNisseirenApi,
  params: FetchJunseiBuhinByKatashikiParams
): Promise<any> {
  return api.getJunseiBuhinByKatashiki(
    params.katashikiParams.katashikiShiteiNo,
    params.katashikiParams.ruibetsuKubunNo,
    params.katashikiParams.junseiKatashiki,
    params.katashikiParams.junseiEngineKatashiki,
    params.katashikiParams.syaryouShiki,
    params.katashikiParams.syaryouShuki,
    params.sknInspectDescriptionId
  ).catch((err: any) => resolveNoContentAsEmpty(err));
}

/*
 * fetchJunseiBuhinByNisseirenWork を WorkAndBuhin.vue から分離し、
 * 型式・日整連・純正 API 呼び出し を親側置換や単体テストで固定するため。
 */
export function fetchJunseiBuhinByNisseirenWork(
  api: WorkBuhinNisseirenApi,
  params: FetchJunseiBuhinByNisseirenWorkParams
): Promise<JunseiBuhinByNisseirenWorkResult> {
  const requestParams = buildJunseiBuhinByNisseirenWorkParams(
    params.katashikiParams,
    params.sknNisseirenCd
  );
  return api.getJunseiBuhinByNisseirenWork(
    params.katashikiParams.katashikiShiteiNo,
    params.katashikiParams.ruibetsuKubunNo,
    params.katashikiParams.junseiKatashiki,
    params.katashikiParams.junseiEngineKatashiki,
    params.katashikiParams.syaryouShiki,
    params.katashikiParams.syaryouShuki,
    requestParams.sknNisseirenCd
  ).then((result: any) => ({
    found: true,
    result
  })).catch(() => ({
    found: false,
    result: null
  }));
}

/*
 * fetchKatashikiList を WorkAndBuhin.vue から分離し、
 * 型式・日整連・純正 API 呼び出し を親側置換や単体テストで固定するため。
 */
export function fetchKatashikiList(
  api: WorkBuhinNisseirenApi,
  katashiki: string,
  engineKatashiki: string,
  katashikiShiteiNo: string,
  ruibetsuKubunNo: string
): Promise<KatashikiSearchResult> {
  return api.getKatashikiList(
    katashiki, engineKatashiki, katashikiShiteiNo, ruibetsuKubunNo
  )
    .then((data: any[]) => ({
      data,
      error: null
    }))
    .catch((error: any) => ({
      data: null,
      error
    }));
}

/*
 * fetchCustomerCarKatashiki を WorkAndBuhin.vue から分離し、
 * 保存済み型式の取得結果を型式検索と同じ成功・失敗形式で扱うため。
 */
export function fetchCustomerCarKatashiki(
  api: WorkBuhinNisseirenApi,
  sknUketsukeExtendsId: number
): Promise<KatashikiSearchResult> {
  return api.getCustomerCarKatashiki(sknUketsukeExtendsId)
    .then((data: any) => ({
      data,
      error: null
    }))
    .catch((error: any) => ({
      data: null,
      error
    }));
}

/*
 * updateKatashikiByParams を WorkAndBuhin.vue から分離し、
 * 型式・日整連・純正 API 呼び出し を親側置換や単体テストで固定するため。
 */
export function updateKatashikiByParams(
  api: WorkBuhinNisseirenApi,
  katashikiParams: KatashikiParams
): Promise<any> {
  return api.updateKatashiki(buildKatashikiUpdatePayload(katashikiParams));
}
